import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Button } from "../ui/button"
import { Card, CardContent } from "../ui/card"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion"
import { Heart, Activity, Stethoscope, Users, Shield, Award, Clock, Phone, Mail, MapPin, Check, Calendar, FileText, Zap, ArrowRight, Baby, TrendingUp, Scan, HeartPulse, BookOpen } from 'lucide-react'
import DoctorCard from "./DoctorCard"
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"

export default function CardiologyPage() {
  const { open: openAppointment } = useAppointment();
  const subspecialties = [
    {
      title: "Interventional Cardiology",
      icon: <Heart className="h-6 w-6 text-apple-teal" />,
      description: "Expertise in angioplasty, stenting, and minimally invasive heart procedures.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Interventional-Cardiology-scaled.jpg",
    },
    {
      title: "Preventive Cardiology",
      icon: <Shield className="h-6 w-6 text-apple-teal" />,
      description: "Early detection, lifestyle management, and risk reduction programs for heart diseases.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Preventive-Cardiology-scaled.jpg",
    },
    {
      title: "Non-Invasive Cardiology",
      icon: <Scan className="h-6 w-6 text-apple-teal" />,
      description: "Advanced cardiac imaging, echocardiography, and stress testing for accurate diagnosis.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Non-Invasive-Cardiology-scaled.jpg",
    },
    {
      title: "Heart Failure Management",
      icon: <HeartPulse className="h-6 w-6 text-apple-teal" />,
      description: "Comprehensive care for chronic and acute heart failure conditions.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Heart-Failure-Management-scaled.jpg",
    },
    {
      title: "Pediatric Cardiology",
      icon: <Baby className="h-6 w-6 text-apple-teal" />,
      description: "Specialized care for congenital and developmental heart conditions in children.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Pediatric-Cardiology-scaled.jpg",
    },
    {
      title: "Cardiac Rehabilitation",
      icon: <TrendingUp className="h-6 w-6 text-apple-teal" />,
      description: "Post-surgery recovery programs for improved heart strength and endurance.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Cardiac-Rehabilitation-scaled.jpg",
    },
  ]

  const procedures = [
    "Coronary Angiography & Angioplasty",
    "Pacemaker & ICD Implantation",
    "Heart Failure & Valve Disease Management",
    "Coronary Artery Bypass Grafting (CABG)",
    "Cardiac Catheterization",
    "2D/3D Echocardiography",
    "TMT (Treadmill Test) & ECG",
    "Holter Monitoring & Cardiac MRI",
  ]

  const whyChoose = [
    {
      icon: <Users className="h-8 w-8 text-apple-teal" />,
      title: "Experienced and Renowned Cardiologists",
      description: "Our team of expert cardiologists and cardiac surgeons brings Years Experience in treating heart diseases.",
    },
    {
      icon: <Clock className="h-8 w-8 text-apple-teal" />,
      title: "24/7 Emergency Heart Care",
      description: "Round-the-clock emergency services and critical support for cardiac emergencies.",
    },
    {
      icon: <Activity className="h-8 w-8 text-apple-teal" />,
      title: "State-of-the-Art Cath Lab",
      description: "Fully equipped catheterization laboratory with advanced technology for accurate diagnosis and treatment.",
    },
    {
      icon: <Heart className="h-8 w-8 text-apple-teal" />,
      title: "Personalized Treatment Programs",
      description: "Customized treatment plans and preventive programs tailored to your specific needs.",
    },
    {
      icon: <MapPin className="h-8 w-8 text-apple-teal" />,
      title: "Convenient Location",
      description: "Easily accessible near Miyapur & Bachupally, Hyderabad for your convenience.",
    },
  ]

  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    Promise.all([
      fetch("https://mamshospitals.mamscollege.com/wp-json/wp/v2/doctor?_embed&per_page=100&page=1").then(r => r.json()),
      fetch("https://mamshospitals.mamscollege.com/wp-json/wp/v2/doctor?_embed&per_page=100&page=2").then(r => r.json()),
    ]).then(([p1, p2]) => {
      const all = [...p1, ...p2];
      const cardiology = all.filter(
        d => d._embedded?.["wp:term"]?.[0]?.[0]?.name === "Cardiology"
      );
      setDoctors(cardiology);
    }).catch(console.error);
  }, []);

  const articles = [
    {
      icon: <Stethoscope className="h-6 w-6 text-apple-teal" />,
      title: "Understanding the Early Signs of a Heart Attack",
      description: "Learn to recognize warning signs and take timely action to save lives.",
    },
    {
      icon: <Heart className="h-6 w-6 text-apple-teal" />,
      title: "How Lifestyle Choices Affect Your Heart Health",
      description: "Discover the impact of diet, exercise, and habits on cardiovascular wellness.",
    },
    {
      icon: <Activity className="h-6 w-6 text-apple-teal" />,
      title: "Advancements in Interventional Cardiology at MAMS Hospitals",
      description: "Explore cutting-edge techniques and technologies in modern cardiac care.",
    },
    {
      icon: <Shield className="h-6 w-6 text-apple-teal" />,
      title: "Simple Tips for Preventive Cardiology",
      description: "Practical advice for maintaining long-term heart wellness and disease prevention.",
    },
  ]

  const faqs = [
    {
      question: "What makes MAMS Hospitals the best cardiology hospital in Hyderabad?",
      answer: "MAMS Hospitals is known for its expert team of cardiologists, cutting-edge cardiac technology, and comprehensive heart care facilities under one roof.",
    },
    {
      question: "What are the common symptoms of a heart problem?",
      answer: "Chest pain, shortness of breath, fatigue, palpitations, and dizziness are early warning signs. Immediate consultation is recommended.",
    },
    {
      question: "Do you provide preventive heart checkups?",
      answer: "Yes, MAMS Hospitals offers complete Cardiac Checkup Packages in Miyapur and Bachupally for early detection and prevention.",
    },
    {
      question: "How can I book an appointment with a cardiologist near me?",
      answer: "You can easily book your appointment online or call our helpline for same-day consultations with our specialists.",
    },
    {
      question: "Do you offer 24x7 emergency cardiac care?",
      answer: "Yes, our hospital has a fully equipped emergency department and Cath Lab for heart attack and cardiac emergency care.",
    },
  ]
const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Cardiology Department",
    "url": "https://mamshospitals.com/cardiology",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/static/media/Cardiology.433dbabae790842ee5cc.jpg",
    "description": "MAMS Hospitals offers advanced cardiology treatment in Hyderabad with expert cardiologists, angioplasty, heart care, ECG, TMT, and emergency cardiac services.",
    "medicalSpecialty": "Cardiovascular",
    "telephone": "+91-7399999981",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },
    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "Coronary Angiography"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Angioplasty"
      },
      {
        "@type": "MedicalProcedure",
        "name": "ECG & TMT"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Pacemaker Implantation"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What makes MAMS Hospitals the best cardiology hospital in Hyderabad?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "MAMS Hospitals provides expert cardiologists, advanced Cath Lab technology, angioplasty, ECG, TMT, and 24x7 emergency cardiac care."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide preventive heart checkups?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals offers preventive cardiology and comprehensive cardiac checkup packages in Hyderabad."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer emergency cardiac care?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides 24x7 emergency heart care and cardiac emergency services."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Cardiology Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/cardiology",
    "description": "Best cardiology hospital in Hyderabad offering angioplasty, heart specialists, ECG, TMT, preventive cardiology, and emergency heart care.",
    "keywords": [
      "Cardiology Hospital Hyderabad",
      "Best Cardiologist in Hyderabad",
      "Heart Specialist Hyderabad",
      "Angioplasty Hyderabad",
      "ECG Test Hyderabad",
      "Cardiac Care Bachupally",
      "Heart Hospital Hyderabad"
    ]
  }
];


  return (
    <>
    <SEO
      title="Best Cardiology Hospital in Hyderabad | Heart Specialists | MAMS Hospitals"
      description="MAMS Hospitals offers advanced cardiology treatment in Hyderabad including angioplasty, ECG, TMT, heart specialists, preventive cardiology & 24x7 emergency heart care."
      keywords="cardiology hospital Hyderabad,
      best cardiologist in Hyderabad,
      heart specialist Hyderabad,
      angioplasty hospital Hyderabad,
      ECG test Hyderabad,
      TMT test Hyderabad,
      cardiac care Bachupally,
      heart hospital near Miyapur,
      preventive cardiology Hyderabad,
      emergency cardiac care Hyderabad,
      Cath Lab Hyderabad,
      pacemaker implantation Hyderabad,
      heart checkup Hyderabad,
      MAMS Hospitals Cardiology"
      schema={schema}
      canonicalUrl="https://mamshospitals.com/departments/cardiology"
      />
    
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-apple-teal/10 to-white py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-apple-teal/10 px-4 py-2 rounded-full mb-6">
                <Heart className="h-5 w-5 text-apple-teal" />
                <span className="text-apple-teal font-medium">Best Cardiology Hospital in Hyderabad</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-apple-gray-500 mb-6">Your Heart, Our Priority</h1>
              <p className="text-lg text-apple-gray-400 mb-8">
                At MAMS Hospitals, we take pride in being one of the Best Cardiology Hospitals in Hyderabad, offering world-class care for every heart. From preventive screenings to complex procedures like angioplasty and cardiac bypass surgery, MAMS Hospitals ensures your heart receives the care it truly deserves.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>
            <div className="relative mt-8 md:mt-0">
              <img
                src="https://mamshospitals.com/static/media/Cardiology.433dbabae790842ee5cc.jpg"
                alt="Cardiology Care at MAMS Hospitals"
                className="rounded-3xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-4 left-2 sm:-bottom-6 sm:-left-6 bg-white p-3 sm:p-6 rounded-2xl shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Heart className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-apple-gray-500">World-Class</div>
                    <div className="text-sm text-apple-gray-400">Heart Care</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-0 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Sub-Specialities in Cardiology</h2>
            <p className="text-apple-gray-400 max-w-2xl mx-auto">
              MAMS Hospitals offers a wide range of subspecialties to ensure complete and holistic cardiac care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((service, index) => (
              <Card
                key={index}
                className="border-apple-gray-100 hover:border-apple-teal transition-all duration-300 hover:shadow-lg rounded-3xl overflow-hidden group"
              >
                <div className="aspect-video relative overflow-hidden bg-apple-gray-50">
                  <img
                    src={service.image || "/placeholder.svg"}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="bg-apple-teal/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-apple-gray-500 mb-2">{service.title}</h3>
                  <p className="text-apple-gray-400 text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-0 bg-apple-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Treatments & Procedures</h2>
            <p className="text-apple-gray-400 max-w-2xl mx-auto">
              Our Cardiology Department is equipped with the latest diagnostic and interventional technology for accurate results and successful outcomes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {procedures.map((procedure, index) => (
              <Card key={index} className="border-apple-gray-100 rounded-2xl">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="bg-apple-teal/10 p-2 rounded-full flex-shrink-0">
                    <Check className="h-5 w-5 text-apple-teal" />
                  </div>
                  <p className="text-apple-gray-500 font-medium">{procedure}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-apple-teal/30 bg-gradient-to-r from-apple-teal/10 to-transparent rounded-3xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="bg-apple-teal/20 p-3 rounded-full flex-shrink-0">
                  <Activity className="h-6 w-6 text-apple-teal" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-apple-gray-500 mb-2">Major Procedures Offered</h3>
                  <p className="text-apple-gray-400">
                    We provide comprehensive diagnostic and treatment services including advanced imaging, minimally invasive interventions, and surgical procedures for all types of heart conditions.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-16 px-0 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">
              Why Choose MAMS Hospitals for Heart Care
            </h2>
            <p className="text-apple-gray-400 max-w-2xl mx-auto">
              Trust Hyderabad's leading cardiac care experts for comprehensive heart health services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => (
              <Card key={index} className="border-apple-gray-100 hover:border-apple-teal transition-all duration-300 rounded-3xl">
                <CardContent className="p-6">
                  <div className="bg-apple-teal/10 p-3 rounded-full w-fit mb-4">{item.icon}</div>
                  <h3 className="text-xl font-semibold text-apple-gray-500 mb-2">{item.title}</h3>
                  <p className="text-apple-gray-400">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-0 bg-apple-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialist</h2>
            <p className="text-apple-gray-400 max-w-2xl mx-auto">
              Our expert cardiologist dedicated to providing exceptional heart care
            </p>
          </div>

          <div className="relative">
            <button
              onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
              {doctors.map(doctor => (
                <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                  <DoctorCard doctor={{
                    name: doctor.title.rendered,
                    slug: doctor.slug,
                    imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                    specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                    designation: doctor.acf?.designation || "",
                    qualification: doctor.acf?.qualification || "",
                    experience: doctor.acf?.experience || "",
                  }} />
                </div>
              ))}
            </div>
            <button
              onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>

          <div className="text-center mt-8">
            <Link to="/doctors">
              <Button variant="outline" className="rounded-full bg-transparent">
                View All Doctors
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* <section className="py-16 px-0 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Recent Articles</h2>
            <p className="text-apple-gray-400 max-w-2xl mx-auto">
              Stay updated with our latest insights on heart health and cardiology
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {articles.map((article, index) => (
              <Card
                key={index}
                className="border-apple-gray-100 hover:border-apple-teal transition-all duration-300 hover:shadow-lg rounded-3xl group cursor-pointer"
              >
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <div className="bg-apple-teal/10 p-3 rounded-full h-fit flex-shrink-0 group-hover:bg-apple-teal/20 transition-colors">
                      {article.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-apple-gray-500 mb-2 group-hover:text-apple-teal transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-apple-gray-400 text-sm mb-3">{article.description}</p>
                      <div className="flex items-center text-apple-teal text-sm font-medium">
                        Read more
                        <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-apple-gray-400">
              Read more on our{" "}
              <Link href="/blog" className="text-apple-teal hover:underline font-medium">
                MAMS Hospitals Blog
              </Link>{" "}
              – your source for reliable health information.
            </p>
          </div>
        </div>
      </section> */}

      <section className="py-16 px-0 bg-apple-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Frequently Asked Questions</h2>
            <p className="text-apple-gray-400 max-w-2xl mx-auto">
              Common questions about cardiac care at MAMS Hospitals
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-apple-gray-100 rounded-2xl px-6 bg-white">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-semibold text-apple-gray-500">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-apple-gray-400 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department </p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  )
}
